/**
 * Yield Sheet arithmetic and shapes, copied from the admin portal's components
 * (insert-update-purchase-order, yield-sheet-summmary, yieldsheet-counting-mode) with the same
 * oddities, so a sheet saved here carries the numbers a sheet saved there would. Pure functions
 * only: scripts/ws8-parity.js runs this same file through a real sheet and compares what comes out
 * with what SQL01 holds. Azure exit WS8.
 *
 * `boxOff` and `bushelOff` stand for Angular's disabled controls. A disabled control is missing
 * from the form's value, except on a finalized sheet, where the save enables everything first.
 */

export const RECEIVERS = [
  { label: 'Ashley Hinman', value: 28 }, { label: 'Carman Pilkerton', value: 11 }, { label: 'Daniel Smart', value: 27 },
  { label: 'Greg Bozman Jr. ', value: 1000 }, { label: 'Louis Hickman', value: 1001 }, { label: 'Matt Cain Jr.', value: 21 },
  { label: 'Matt Cain Sr.', value: 2 }, { label: 'Montel Douglas', value: 1002 }, { label: 'Paul Klimm', value: 1003 },
  { label: 'Reginald Corbin', value: 1004 }, { label: 'Warren Willey', value: 1005 }, { label: 'Scottie Tawes', value: 1006 },
];

const IMAGE_TYPES = ['jpg', 'jpeg', 'jpe', 'jif', 'jfif', 'jfi', 'png', 'gif', 'webp', 'tiff', 'tif', 'raw', 'arw', 'cr2', 'nrw', 'k25', 'psd', 'bmp', 'dib',
  'heif', 'heic', 'ind', 'indd', 'indt', 'jp2', 'j2k', 'jpf', 'jpx', 'jpm', 'mj2', 'svg', 'svgz', 'ai', 'eps'];

export function fileType(url) {
  const extension = String(url || '').split('/').pop().split('.').pop().toLowerCase();
  if (IMAGE_TYPES.includes(extension)) return 'image';
  if (extension === 'pdf') return 'pdf';
  return 'none';
}

const n = (v) => (v == null ? 0 : Number(v));
const day = (v, none) => (v == null ? none : String(v).slice(0, 10));
const NO_DATE = '0001-01-01';

// YieldSheet/GetPurchaseOrderById: what the API's Populate* methods do with the six result sets.
export function assembleYieldSheet({ po, items, transportation, sheet, sheetItems, summaries }) {
  const p = po[0];
  const t = transportation[0];
  const s = sheet[0];
  return {
    purchaseOrder: p ? {
      purchaseOrderId: n(p.PurchaseOrderId), igrId: n(p.IGRId), crabSupplierId: n(p.CrabSupplierId), status: n(p.StatusId),
      dateReceived: day(p.DateReceived, NO_DATE), dateProcessed: day(p.DateProcessed, NO_DATE), unitOfMeasureId: n(p.UnitOfMeasureId),
      invoiceAmount: n(p.InvoiceAmount), totalAmount: n(p.TotalAmount), totalCrabs: Math.round(n(p.TotalCrabs)), totalYield: n(p.TotalYield),
      receiverId: n(p.ReceiverId), invoiceDate: day(p.InvoiceDate, NO_DATE), invoiceNumber: p.InvoiceNumber == null ? '' : p.InvoiceNumber,
      note: p.Note == null ? '' : p.Note, invoiceURL: null,
    } : { purchaseOrderId: 0, igrId: 0, crabSupplierId: 0, status: 0, dateReceived: null, dateProcessed: null, unitOfMeasureId: 0, invoiceAmount: 0, totalAmount: 0, totalCrabs: 0, totalYield: 0, receiverId: 0, invoiceDate: null, invoiceNumber: null, note: null, invoiceURL: null },
    purchaseOrderItems: items.map((r) => ({
      purchaseOrderItemId: n(r.PurchaseOrderItemId), purchaseOrderId: n(r.PurchaseOrderId), boxQuantity: n(r.BoxQuantity), weight: n(r.Weight),
      bushelQuantity: n(r.BushelQuantity), description: n(r.Description), itemTransportationCost: n(r.TransportationCost), price: n(r.Price), totalPrice: n(r.TotalPrice),
    })),
    purchaseOrderTransportation: {
      purchaseOrderTransportationId: t ? n(t.PurchaseOrderTransportationId) : 0, purchaseOrderId: t ? n(t.PurchaseOrderId) : 0,
      transportationCost: t ? n(t.TransportationCost) : 0, transportationInvoiceDate: t ? day(t.TransportationInvoiceDate, NO_DATE) : NO_DATE, transportationInvoiceURL: null,
    },
    yieldSheet: {
      yieldSheetId: s ? n(s.YieldSheetId) : 0, purchaseOrderId: s ? n(s.PurchaseOrderId) : 0, dead: s ? n(s.Dead) : 0, dateCooked: s ? day(s.DateCooked, NO_DATE) : NO_DATE,
      totalYield: s ? n(s.TotalYield) : 0, totalMaleYielded: s ? n(s.TotalMaleYielded) : 0, totalFemaleYielded: s ? n(s.TotalFemaleYielded) : 0,
    },
    yieldSheetItems: sheetItems.map((r) => ({
      yieldSheetItemId: n(r.YieldSheetItemId), yieldSheetId: n(r.YieldSheetId), gradedId: n(r.GradedId), gradedName: r.GradeName, gradedType: r.Type,
      tally: r.Tally == null ? '' : r.Tally, live: n(r.Live), retailPrice: n(r.RetailPrice),
    })),
    // PopulateYieldSheetSummary leaves Dead out, so it reads 0 and the next save writes 0.
    yieldSheetSummary: summaries.map((r) => ({
      yieldSheetSummaryId: n(r.YieldSheetSummaryId), yieldSheetId: n(r.YieldSheetId), typeId: n(r.TypeId), typeName: r.TypeName, total: n(r.Total), dead: 0, heldForLive: n(r.HeldForLive),
    })),
  };
}

// GetPurchaseOrderInvoiceImagesById skips a row with no file and puts a PDF under Large.
export const invoiceImageList = (rows) => rows.filter((r) => r.imageUrl != null).map((r, i) => ({
  dataId: r.dataId, imageId: i,
  imageUrl: r.imageUrl.toLowerCase().endsWith('.pdf') ? `Files/Images/PurchaseOrder/Large/${r.imageUrl}` : `Files/Images/PurchaseOrder/${r.imageUrl}`,
}));
export const transportImageList = (rows) => rows.map((r, i) => ({ dataId: r.dataId, imageId: i, imageUrl: `Files/Images/PurchaseOrder/${r.imageUrl == null ? '' : r.imageUrl}` }));

const gradedRows = (rows) => rows.map((item) => {
  const tallyValues = item.tally.trim() !== '' ? item.tally.split(',').map((value) => parseFloat(value.trim())) : [];
  const tallySum = tallyValues.reduce((a, b) => a + b, 0);
  const liveCount = item.live === 0 ? 0 : item.live;
  return { yieldSheetItemId: item.yieldSheetItemId, gradedId: item.gradedId, gradedName: item.gradedName, tally: '', live: item.live === 0 ? '' : item.live, totalGraded: tallySum === 0 ? liveCount : tallySum + liveCount, tallyValues };
});

const blankDate = (v, none) => {
  const d = v == null ? v : String(v).split('T')[0];
  return d === none ? '' : d;
};

// onUnitOfMeasureChange: one of the two quantities goes to zero and switches off.
export function applyUnit(quantities, selected) {
  if (String(selected) === '1') return quantities.map((q) => ({ ...q, bushelQty: 0, bushelOff: true, boxOff: false }));
  if (String(selected) === '2') return quantities.map((q) => ({ ...q, boxQty: 0, boxOff: true, bushelOff: false }));
  return quantities;
}

export function newQuantity(selectedUnit) {
  const box = Number(selectedUnit) !== 2;
  const bu = Number(selectedUnit) !== 1;
  return { purchaseOrderItemId: '', boxQty: '', weight: '', bushelQty: '', itemTransportationCost: '', description: '', price: '', totalPrice: '', boxOff: !box, bushelOff: !bu };
}

export const blankGraded = (graded, type) => graded.filter((g) => g.type === type).map((g) => ({ yieldSheetItemId: 0, gradedId: g.id, gradedName: g.name, tally: '', live: '', totalGraded: '', tallyValues: [] }));

export function blankForm(lookups) {
  return {
    suplier: '', dateRecevied: '', dateProcessed: '', unitOfMeasure: '', recevierId: '', invoiceDate: '', invoiceNumber: '', invoiceAmount: '',
    transparentId: '', transparentInvoiceDate: '', yieldSheetId: '', dateCooked: '', notes: '', dead: '',
    quantities: [newQuantity(0)],
    maleGradedRows: blankGraded(lookups.graded, 'male'), femaleGradedRows: blankGraded(lookups.graded, 'female'),
    yieldSummary: lookups.summaryTypes.map((x) => ({ yieldSheetSummaryId: 0, typeId: x.value, typeName: x.label, total: '', dead: '', holdForLive: '' })),
  };
}

// showDataInForm. The returned flags are the component's: readonlyMode, IsLinkedIGR, saveAfterFinal.
export function formFromSheet(data) {
  const po = data.purchaseOrder;
  const tr = data.purchaseOrderTransportation;
  const ys = data.yieldSheet;
  const unit = po.unitOfMeasureId;
  const quantities = data.purchaseOrderItems.map((item) => ({
    purchaseOrderItemId: item.purchaseOrderItemId, purchaseOrderId: item.purchaseOrderId, boxQty: item.boxQuantity, weight: item.weight, bushelQty: item.bushelQuantity,
    description: item.description, itemTransportationCost: item.itemTransportationCost, price: item.price, totalPrice: `$${item.totalPrice.toFixed(2)}`, boxOff: false, bushelOff: false,
  }));
  return {
    form: {
      suplier: po.crabSupplierId, dateRecevied: blankDate(po.dateReceived, NO_DATE), dateProcessed: blankDate(po.dateProcessed, NO_DATE), unitOfMeasure: unit,
      recevierId: po.receiverId, invoiceDate: blankDate(po.invoiceDate, NO_DATE), invoiceNumber: po.invoiceNumber, invoiceAmount: po.invoiceAmount === 0 ? '' : po.invoiceAmount.toFixed(2),
      transparentId: tr.purchaseOrderTransportationId, transparentInvoiceDate: blankDate(tr.transportationInvoiceDate, '1753-01-01'), yieldSheetId: ys.yieldSheetId,
      dateCooked: blankDate(ys.dateCooked, NO_DATE), notes: po.note, dead: ys.dead,
      quantities: applyUnit(quantities, String(unit)),
      maleGradedRows: gradedRows(data.yieldSheetItems.filter((x) => x.gradedType === 'male')),
      femaleGradedRows: gradedRows(data.yieldSheetItems.filter((x) => x.gradedType === 'female')),
      yieldSummary: data.yieldSheetSummary.map((item) => ({
        yieldSheetSummaryId: item.yieldSheetSummaryId, typeId: item.typeId, typeName: item.typeName, total: item.total === 0 ? '' : item.total,
        dead: item.dead === 0 ? '' : item.dead, holdForLive: item.heldForLive === 0 ? '' : item.heldForLive,
      })),
    },
    selectedUnit: unit,
    readonlyMode: po.status === 1,
    saveAfterFinal: po.status === 1,
    igrLinked: po.igrId > 0,
  };
}

// updateTotalAmountAndCrabs and its calculate* helpers.
/* eslint-disable eqeqeq */
export function orderTotals(quantities, selectedUnit) {
  const sum = { amount: 0, crab: 0, tcost: 0, tunits: 0, mcount: 0, fcount: 0, mprice: 0, fprice: 0, ftcost: 0, mtcost: 0 };
  quantities.forEach((q) => {
    const boxQty = q.boxQty || 0;
    const bushelQty = q.bushelQty || 0;
    const weight = q.weight || 0;
    const price = q.price || 0;
    const cost = q.itemTransportationCost || 0;
    const units = boxQty + bushelQty;
    const line = units * price * weight;
    sum.amount += line;
    sum.crab += selectedUnit == 1 ? units * weight : bushelQty;
    sum.tcost += units * cost;
    sum.tunits += units;
    const male = q.description == 1 || q.description == 2;
    const female = q.description == 3;
    if (male) { sum.mcount += units; sum.mprice += line; }
    if (female) { sum.fcount += units; sum.fprice += line; }
    const carried = (boxQty > 0 ? boxQty * q.itemTransportationCost : bushelQty * q.itemTransportationCost) || 0;
    if (female) sum.ftcost += carried; else sum.mtcost += carried;
  });
  return {
    totalAmount: sum.amount, totalCrab: sum.crab, transportationAmount: parseFloat(sum.tcost.toFixed(2)), totalBoxOrBushel: sum.tunits,
    totalMaleBoxOrBushel: sum.mcount, totalFemaleBoxOrBushel: sum.fcount, totalMalePriceOfOrder: sum.mprice, totalFemalePriceOfOrder: sum.fprice,
    totalMaleTransportationCosts: sum.mtcost, totalFemaleTransportationCosts: sum.ftcost,
  };
}

// calculateTotalYielded. Trash (5 and 8) does not count.
export function yieldTotals(maleRows, femaleRows, totalBoxOrBushel) {
  let male = 0;
  let female = 0;
  maleRows.forEach((r) => { if ((+r.gradedId || 0) !== 5) male += +r.totalGraded || 0; });
  femaleRows.forEach((r) => { if ((+r.gradedId || 0) !== 8) female += +r.totalGraded || 0; });
  const totalYieldedPercentage = totalBoxOrBushel > 0 ? (((female + male) / totalBoxOrBushel) * 100).toFixed(2) : '0';
  const totalMaleYielded = parseFloat(male.toFixed(2));
  const totalFemaleYielded = parseFloat(female.toFixed(2));
  return { totalMaleYielded, totalFemaleYielded, totalYieldedPercentage, totalYielded: totalFemaleYielded + totalMaleYielded };
}

const calculateTotalGraded = (tally, live) => parseFloat(tally.reduce((a, b) => a + b, 0).toFixed(2)) + live;

export function addTally(rows, index, value) {
  if (!(value > 0)) return rows;
  return rows.map((r, i) => {
    if (i !== index) return r;
    const tallyValues = [...r.tallyValues, parseFloat(value.toFixed(2))];
    return { ...r, tallyValues, totalGraded: calculateTotalGraded(tallyValues, +r.live) };
  });
}

export function removeTally(rows, index, value) {
  return rows.map((r, i) => {
    if (i !== index) return r;
    const at = r.tallyValues.indexOf(value);
    if (at === -1) return r;
    const tallyValues = r.tallyValues.filter((_, j) => j !== at);
    return { ...r, tallyValues, totalGraded: calculateTotalGraded(tallyValues, +r.live) };
  });
}

export const setLive = (rows, index, live) => rows.map((r, i) => (i === index ? { ...r, live, totalGraded: calculateTotalGraded(r.tallyValues, +live) } : r));

// calculateTotalPrice. It takes the bushel figure whenever there is one, so a finalized LBS sheet,
// whose bushels read 0 once the form is enabled to save, saves a line total of 0. Kept.
export function calculateTotalPrice(item) {
  const { boxQty, bushelQty, price, weight } = item;
  let quantity = null;
  if (boxQty !== null && boxQty !== '' && boxQty !== undefined) quantity = boxQty;
  if (bushelQty !== null && bushelQty !== '' && bushelQty !== undefined) quantity = bushelQty;
  if (quantity === null) return 0;
  return parseFloat((quantity * price * weight).toFixed(2));
}

// A row as the form's value holds it: a disabled control is missing unless the sheet is finalized.
export const quantityValue = (q, enabled) => {
  const { boxOff, bushelOff, ...v } = q;
  if (!enabled) {
    if (boxOff) delete v.boxQty;
    if (bushelOff) delete v.bushelQty;
  }
  return v;
};

// insertUpdatePurchaseOrdersubmit: the body of YieldSheet/InsertUpdatePurchaseOrder.
export function buildSaveModel({ form, purchaseOrderId, status, selectedUnit, readonlyMode, invoiceImages, transportImages, subject = null, body = null }) {
  const t = orderTotals(form.quantities, selectedUnit);
  const y = yieldTotals(form.maleGradedRows, form.femaleGradedRows, t.totalBoxOrBushel);
  const items = form.quantities.map((q) => quantityValue(q, readonlyMode)).map((item) => ({
    purchaseOrderItemId: item.purchaseOrderItemId, purchaseOrderId, boxQuantity: item.boxQty, weight: item.weight, itemTransportationCost: item.itemTransportationCost,
    bushelQuantity: item.bushelQty, description: item.description, price: item.price, totalPrice: calculateTotalPrice(item),
  }));
  const graded = [...form.maleGradedRows, ...form.femaleGradedRows].map((item) => ({
    yieldSheetItemId: item.yieldSheetItemId, yieldSheetId: 0, gradedId: item.gradedId, tally: item.tallyValues.join(','), live: item.live, retailPrice: 0,
  }));
  return {
    PurchaseOrder: {
      purchaseOrderId, crabSupplierId: form.suplier, dateReceived: form.dateRecevied, dateProcessed: form.dateProcessed, unitOfMeasureId: form.unitOfMeasure,
      totalAmount: t.totalAmount, totalCrabs: t.totalCrab, receiverId: form.recevierId, invoiceDate: form.invoiceDate, invoiceNumber: form.invoiceNumber,
      invoiceURL: invoiceImages, invoiceAmount: form.invoiceAmount, note: form.notes, status,
    },
    PurchaseOrderItems: items,
    PurchaseOrderTransportation: {
      purchaseOrderTransportationId: form.transparentId, purchaseOrderId, transportationCost: t.transportationAmount,
      transportationInvoiceDate: form.transparentInvoiceDate, transportationInvoiceURL: transportImages,
    },
    YieldSheet: {
      yieldSheetId: form.yieldSheetId, purchaseOrderId, dateCooked: form.dateCooked, totalYield: y.totalYielded, totalMaleYielded: y.totalMaleYielded,
      totalFemaleYielded: y.totalFemaleYielded, dead: form.dead,
    },
    YieldSheetItems: graded,
    YieldSheetSummary: form.yieldSummary.map((item) => ({
      yieldSheetSummaryId: item.yieldSheetSummaryId, yieldSheetId: 0, typeId: item.typeId, total: item.total, dead: item.dead, heldForLive: item.holdForLive,
    })),
    Subject: subject,
    Body: body,
  };
}

export const countingItems = (form, yieldSheetId) => [...form.maleGradedRows, ...form.femaleGradedRows].map((item) => ({
  yieldSheetItemId: item.yieldSheetItemId, yieldSheetId, gradedId: item.gradedId, tally: item.tallyValues.join(','), live: item.live, retailPrice: 0,
}));

function retailValue(rows, prices) {
  const list = [];
  rows.forEach((item) => {
    const total = item.tallyValues.reduce((a, b) => a + b, 0) + (item.live || 0);
    const hit = list.find((x) => x.gradedId === item.gradedId);
    if (hit) hit.total += total; else list.push({ gradedId: item.gradedId, total });
  });
  let sum = 0;
  list.forEach((g) => {
    const price = prices.find((p) => p.gradedId === g.gradedId);
    if (price) sum += price.price * g.total;
  });
  return sum;
}

// finalizeYieldSheet: the words for the low yield email. Note a ROI line replaces the subject
// built so far, where the body adds to it. Kept.
export function finalizeWarnings({ form, totals, yields, prices }) {
  let subject = '';
  let body = '';
  const { dead } = form;
  if (dead > 0 && totals.totalBoxOrBushel > 0 && (dead / totals.totalBoxOrBushel) * 100 > 20) {
    subject += 'The death loss is over 20% ';
    body += 'The death loss is over 20% ';
  }
  if (totals.totalMaleBoxOrBushel > 0 && yields.totalMaleYielded > 0 && (yields.totalMaleYielded / totals.totalMaleBoxOrBushel) * 100 < 75) {
    subject = subject === '' ? 'The male yield is under 75%' : `${subject} and the male yield is under 75%`;
    body = body === '' ? 'The male yield is under 75%' : `${body} and the male yield is under 75%`;
  }
  if (totals.totalFemaleBoxOrBushel > 0 && yields.totalFemaleYielded > 0 && (yields.totalFemaleYielded / totals.totalFemaleBoxOrBushel) * 100 < 75) {
    subject = subject === '' ? 'The female yield is under 75%' : `${subject} and the female yield is under 75%`;
    body = body === '' ? 'The female yield is under 75%' : `${body} and the female yield is under 75%`;
  }
  const male = retailValue(form.maleGradedRows, prices);
  if (totals.totalMalePriceOfOrder > 0 && male) {
    const landed = totals.totalMalePriceOfOrder + totals.totalMaleTransportationCosts;
    if (((male - landed) / landed) * 100 < 90) {
      subject = subject === '' ? 'The male ROI is under 90%' : 'and the ROI is under 90%';
      body = body === '' ? 'The male ROI is under 90%' : `${body} and the ROI is under 90%`;
    }
  }
  const female = retailValue(form.femaleGradedRows, prices);
  if (totals.totalFemalePriceOfOrder > 0 && female) {
    if ((female / (totals.totalFemalePriceOfOrder + totals.totalFemaleTransportationCosts) - 1) * 100 < 90) {
      subject = subject === '' ? 'The female ROI is under 90%' : 'and the female ROI is under 90%';
      body = body === '' ? 'The female ROI is under 90%' : `${body} and the female ROI is under 90%`;
    }
  }
  return { subject, body };
}

const csvSum = (tally) => tally.split(',').reduce((sum, s) => { const v = parseFloat(s); return sum + (Number.isNaN(v) ? 0 : v); }, 0);

// populateDataInSummary and everything it calls.
export function buildSummary(data, graded) {
  const s = {
    isFinilized: data.purchaseOrder.status !== 1, totalMaleSteam: 0, totalMaleSteamValue: 0, stratMaleCount: 0, startFemaleCount: 0, totalFemaleSteam: 0,
    totalFemaleSteamValue: 0, itemsTotalPrice: 0, totalFreight: 0, totalTransportationCost: 0, totalMaleFreight: 0, totalFemaleFreight: 0,
    maleGradedTableData: [], femaleGradedTableData: [], purchaseOrderItems: [], validateInvoiceAmountData: [],
  };
  s.dateReceived = data.purchaseOrder.dateReceived == null ? data.purchaseOrder.dateReceived : String(data.purchaseOrder.dateReceived).split('T')[0];
  s.note = data.purchaseOrder.note;
  s.totalCrab = data.purchaseOrder.totalCrabs;
  const lines = data.purchaseOrderItems;
  for (let i = 0; i < 3; i += 1) {
    const hit = lines.find((x) => x.description === i + 1);
    s.purchaseOrderItems.push({ description: i + 1, qty: hit ? (hit.boxQuantity === 0 ? hit.bushelQuantity : hit.boxQuantity) : 0 });
  }
  s.totalMaleBoxOrBushels = s.purchaseOrderItems.filter((x) => x.description === 1 || x.description === 2).reduce((a, b) => a + b.qty, 0);
  s.totalFemaleBoxOrBushels = s.purchaseOrderItems.filter((x) => x.description === 3).reduce((a, b) => a + b.qty, 0);
  s.dead = data.yieldSheet.dead;
  const items = data.yieldSheetItems;
  for (let i = 0; i < items.length; i += 1) {
    const grade = graded.find((g) => g.id === i + 1);
    const item = items.find((x) => x.gradedId === i + 1);
    if (grade && item) {
      const tallySum = csvSum(item.tally);
      const { live, retailPrice } = item;
      const row = { itemId: item.yieldSheetItemId, gradedby: grade.name, tallySum, live, retailPrice };
      if (i < 5) {
        if (grade.name !== 'Trash') { s.totalMaleSteam += tallySum + live; s.totalMaleSteamValue += parseFloat(((tallySum + live) * retailPrice).toFixed(2)); }
        s.maleGradedTableData.push(row);
      } else {
        if (grade.name !== 'Trash') { s.totalFemaleSteam += tallySum + live; s.totalFemaleSteamValue += parseFloat(((tallySum + live) * retailPrice).toFixed(2)); }
        s.femaleGradedTableData.push(row);
      }
    }
  }
  s.invoiceAmount = data.purchaseOrder.invoiceAmount.toFixed(2);
  s.totalBoxOrBushels = lines.reduce((a, x) => a + x.boxQuantity + x.bushelQuantity, 0);
  for (let i = 0; i < 3; i += 1) {
    const item = lines.find((x) => x.description === i + 1);
    if (item) {
      const qty = item.boxQuantity === 0 ? item.bushelQuantity : item.boxQuantity;
      const freight = item.totalPrice + qty * item.itemTransportationCost;
      s.itemsTotalPrice += item.totalPrice;
      s.totalTransportationCost += qty * item.itemTransportationCost;
      s.totalFreight += freight;
      if (i === 2) { s.totalFemaleFreight += freight; s.startFemaleCount += qty; } else { s.totalMaleFreight += freight; s.stratMaleCount += qty; }
      s.validateInvoiceAmountData.push({ size: item.description, weight: item.weight, price: item.price, totalPrice: item.totalPrice, transportationCost: item.itemTransportationCost, freight });
    } else {
      s.validateInvoiceAmountData.push({ size: String(i + 1), weight: 0, price: 0, totalPrice: 0, transportationCost: 0, freight: 0 });
    }
  }
  return s;
}

// The Send Email button on the summary. Its wording differs from finalize; both are copied.
export function summaryWarnings(s) {
  let subject = '';
  let body = '';
  if (s.dead > 0 && s.totalBoxOrBushels > 0 && (s.dead / s.totalBoxOrBushels) * 100 > 20) {
    subject = 'The death loss is over 20%';
    body += 'The death loss is over 20%';
  }
  if (s.totalMaleBoxOrBushels > 0 && s.totalMaleSteam > 0 && (s.totalMaleSteam / s.totalMaleBoxOrBushels) * 100 < 75) {
    subject = subject === '' ? 'The male yield is under 75%' : `${subject}and the male yield is under 75%`;
    body = body === '' ? 'The Yield is under 75%' : `${body} and the Yield is under 75%`;
  }
  if (s.totalFemaleBoxOrBushels > 0 && s.totalFemaleSteam > 0 && (s.totalFemaleSteam / s.totalFemaleBoxOrBushels) * 100 < 75) {
    subject = subject === '' ? 'The female yield is under 75%' : `${subject}and the female yield is under 75%`;
    body = body === '' ? 'The Yield is under 75%' : `${body} and the Yield is under 75%`;
  }
  if (s.totalMaleSteamValue > 0 && s.totalMaleFreight && (s.totalMaleSteamValue / s.totalMaleFreight - 1) * 100 < 90) {
    subject = subject === '' ? 'The male ROI is under 90%' : 'and the ROI is under 90%';
    body = body === '' ? 'The male ROI is under 90%' : `${body} and the ROI is under 90%`;
  }
  if (s.totalFemaleSteamValue > 0 && s.totalFemaleFreight && (s.totalFemaleSteamValue / s.totalFemaleFreight - 1) * 100 < 90) {
    subject = subject === '' ? 'The female ROI is under 90%' : 'and the female ROI is under 90%';
    body = body === '' ? 'The female ROI is under 90%' : `${body} and the female ROI is under 90%`;
  }
  return { subject, body };
}

// Angular's number and currency pipes: a missing or impossible figure prints nothing.
const blankOr = (v, fmt) => {
  if (v === null || v === undefined || v === '' || Number.isNaN(Number(v))) return '';
  if (!Number.isFinite(Number(v))) return Number(v) < 0 ? '-\u221e' : '\u221e';
  return fmt(Number(v));
};
export const num2 = (v) => blankOr(v, (x) => x.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
export const money = (v) => blankOr(v, (x) => x.toLocaleString('en-US', { style: 'currency', currency: 'USD' }));
export const usDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return y === '0001' ? '' : `${m}/${d}/${y}`;
};

// One line of the list: the percentages purchase-order-list.component.html works out.
export const listRatios = (r) => ({
  yieldPct: `${num2(((r.totalMaleTally + r.totalFemaleTally) / r.totalCrabCount) * 100)} ${r.totalMaleTally || r.totalFemaleTally ? '%' : ''}`,
  male: `${num2((r.totalMaleRetailPrice / r.totalMaleFright - 1) * 100)} ${r.totalMaleRetailPrice && r.totalMaleFright ? '%' : ''}`,
  female: `${num2((r.totalFemaleRetailPrice / r.totalFemaleFright - 1) * 100)} ${r.totalFemaleRetailPrice && r.totalFemaleFright ? '%' : ''}`,
  total: `${num2(((r.totalFemaleRetailPrice + r.totalMaleRetailPrice) / (r.totalMaleFright + r.totalFemaleFright) - 1) * 100)} ${(r.totalFemaleRetailPrice || r.totalMaleRetailPrice) && (r.totalMaleFright || r.totalFemaleFright) ? '%' : ''}`,
});

// The summary pop-up as tables, for the emailed PDF.
export function summaryTables(s, supplierName) {
  const graded = (title, rows) => ({
    columns: [title, 'STEAM', 'Live', 'Total', 'Crab Place Retail ($)', 'Valuation'],
    rows: rows.map((r) => [r.gradedby, num2(r.tallySum), num2(r.live), num2(r.tallySum + r.live), String(r.retailPrice), money((r.tallySum + r.live) * r.retailPrice)]),
  });
  const male = graded('Male Steam Count (Start)', s.maleGradedTableData);
  male.rows.push(['Male Steam Count (End)', '', '', num2(s.totalMaleSteam), '', ''], ['Total Male Value', '', '', '', '', money(s.totalMaleSteamValue)]);
  const female = graded('Female Steam Count (Start)', s.femaleGradedTableData);
  female.rows.push(['Female Steam Count (End)', '', '', num2(s.totalFemaleSteam), '', ''], ['Total Female Value', '', '', '', '', money(s.totalFemaleSteamValue)]);
  return [
    {
      columns: ['Live Count (Start)', '', 'Date:', s.dateReceived],
      rows: [['Vendor Name', supplierName, '', 'Total'], ['Total Count', s.totalBoxOrBushels, '', s.totalBoxOrBushels],
        ...s.purchaseOrderItems.map((x) => [`#${x.description}`, x.qty, '', x.qty]), ['Dead', s.dead, '', s.dead], ['', '', 'Total', s.totalBoxOrBushels - s.dead]],
    },
    {
      columns: ['Crab Size', 'Box/Bushel Weight', 'Price', 'Invoiced Price', 'Transportation Cost', 'Total Price w/ Freight'],
      rows: [['Validated Invoice Amount', '', '', '', '', money(s.invoiceAmount)],
        ...s.validateInvoiceAmountData.map((x) => [`#${x.size}`, x.weight, money(x.price), money(x.totalPrice), money(x.transportationCost), money(x.freight)]),
        ['Invoice Total', '', '', money(s.itemsTotalPrice), money(s.totalTransportationCost), money(s.totalFreight)]],
    },
    male,
    female,
    { columns: ['Total Invoice Value', money(s.totalMaleSteamValue + s.totalFemaleSteamValue)], rows: [] },
    {
      columns: ['Male Count', 'End Count', 'Female Count', 'End Count'],
      rows: [[num2(s.stratMaleCount), num2(s.totalMaleSteam), num2(s.startFemaleCount), num2(s.totalFemaleSteam)]],
    },
    {
      columns: ['Male Yield %', 'Female Yield %', 'Total Yield %', 'Retail Ratio(Males) Goal 100%+', 'Retail Ratio(Females) Goal 100%+'],
      rows: [[`${num2((s.totalMaleSteam / s.stratMaleCount) * 100)} %`, `${num2((s.totalFemaleSteam / s.startFemaleCount) * 100)} %`,
        `${num2(((s.totalFemaleSteam + s.totalMaleSteam) / (s.startFemaleCount + s.stratMaleCount)) * 100)} %`,
        `${num2((s.totalMaleSteamValue / s.totalMaleFreight - 1) * 100)} %`, `${num2((s.totalFemaleSteamValue / s.totalFemaleFreight - 1) * 100)} %`]],
    },
  ];
}